import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, XCircle, Loader2, Mail, Home, AlertCircle } from "lucide-react";
import { NewsletterService } from "@/services/newsletterService";
import { useToast } from "@/hooks/use-toast";

type UnsubscribeState = 'loading' | 'input' | 'processing' | 'success' | 'error';

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [state, setState] = useState<UnsubscribeState>('loading');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalNavHeight, setTotalNavHeight] = useState(112);

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  // Check for email parameter in URL
  useEffect(() => {
    const emailParam = searchParams.get('email');
    
    if (emailParam) {
      setEmail(emailParam);
      handleUnsubscribe(emailParam);
    } else {
      setState('input');
    }
  }, [searchParams]);

  const handleUnsubscribe = async (emailToUnsubscribe: string) => {
    setState('processing');
    setErrorMessage('');

    try {
      const result = await NewsletterService.unsubscribe(emailToUnsubscribe);
      
      if (result.success) {
        setState('success');
        toast({
          title: "Unsubscribed Successfully",
          description: result.message,
        });
      } else {
        setState('error');
        setErrorMessage(result.message);
      }
    } catch (error) {
      setState('error');
      setErrorMessage('Something went wrong. Please try again later.');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    await handleUnsubscribe(email.trim());
    setIsSubmitting(false);
  };

  return (
    <div 
      className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
      style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
    >
      <Card className="w-full max-w-md mx-auto shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900">
            Newsletter Unsubscribe
          </CardTitle>
          <CardDescription className="text-gray-600">
            {state === 'loading' && 'Processing your request...'}
            {state === 'input' && 'Enter your email to unsubscribe from our newsletter'}
            {state === 'processing' && 'Unsubscribing you from our newsletter...'}
            {state === 'success' && 'You have been successfully unsubscribed'}
            {state === 'error' && 'There was a problem with your request'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {state === 'loading' && (
            <div className="flex flex-col space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-jamaica-green mx-auto" />
              <p className="text-sm text-gray-600 text-center">Processing your unsubscribe request...</p>
            </div>
          )}

          {state === 'input' && (
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="w-full"
                />
              </div>

              {errorMessage && (
                <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {errorMessage}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Unsubscribing...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Unsubscribe from Newsletter
                  </>
                )}
              </Button>

              <div className="text-center">
                <Button 
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="text-sm text-gray-600 hover:text-jamaica-green"
                >
                  Cancel and return to home
                </Button>
              </div>
            </form>
          )}

          {state === 'processing' && (
            <div className="flex flex-col space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-jamaica-green mx-auto" />
              <p className="text-sm text-gray-600 text-center">
                Unsubscribing {email} from our newsletter...
              </p>
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">Successfully Unsubscribed</h3>
                <p className="text-sm text-gray-600">
                  {email} has been successfully removed from our newsletter list. You won't receive any more newsletter emails from us.
                </p>
                <p className="text-xs text-gray-500">
                  If you change your mind, you can always subscribe again from our website.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/')}
                className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white"
              >
                <Home className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </div>
          )}

          {state === 'error' && (
            <div className="flex flex-col space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">Unsubscribe Failed</h3>
                <p className="text-sm text-gray-600">
                  {errorMessage || 'We encountered an error while trying to unsubscribe you from our newsletter.'}
                </p>
                <p className="text-sm text-gray-600">
                  If you continue to have problems, please{" "}
                  <a 
                    href="mailto:support@jampackd.store?subject=Newsletter%20Unsubscribe%20Issue&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20am%20having%20trouble%20unsubscribing%20from%20the%20newsletter:%0D%0A%0D%0AEmail:%20{email}%0D%0A%0D%0APlease%20help%20me%20unsubscribe.%0D%0A%0D%0AThank%20you!" 
                    className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
                  >
                    contact our support team
                  </a>
                  .
                </p>
              </div>
              <div className="flex flex-col w-full space-y-2">
                <Button 
                  onClick={() => setState('input')}
                  variant="outline"
                  className="w-full"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate('/')}
                  className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Return to Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Unsubscribe; 